import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Menu, X } from 'lucide-react';
import { faqSections, Question, filterQuestions } from './faqData';


const PrimeLogo = ({ size = 'large' }) => {
  const logoStyle = {
    fontSize: size === 'large' ? '1.5rem' : '1.2rem',
    fontWeight: 'bold',
    color: '#432973',
    fontFamily: 'Arial, sans-serif',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  };
  
  const iconStyle = {
    width: size === 'large' ? '28px' : '22px',
    height: size === 'large' ? '28px' : '22px',
    background: 'linear-gradient(135deg, #432973, #6d4eb0)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: size === 'large' ? '18px' : '14px',
    fontWeight: 'bold'
  };
  
  const sloganStyle = {
    fontSize: size === 'large' ? '0.7rem' : '0.6rem',
    color: '#432973',
    marginTop: '2px',
    fontStyle: 'italic'
  };

  return (
    <div>
      <div style={logoStyle}>
        <div style={iconStyle}>
          <span>✓</span>
        </div>
        <span>PRIME CURSOS</span>
      </div>
      <div style={sloganStyle}>UM NOVO CONCEITO EM ENSINO A DISTÂNCIA</div>
    </div>
  );
};

const PrimeCursosFAQ = () => {
  const [activeSection, setActiveSection] = useState('sobre');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedQuestions, setExpandedQuestions] = useState<{[key: string]: boolean}>({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 768 : true);

  // Detect screen size
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // Collect all questions for search functionality
    const questions: Question[] = [];

    // Substitua "as FAQSections" para tratar faqSections como o tipo correto
    Object.keys(faqSections).forEach(sectionKey => {
      (faqSections)[sectionKey].questions.forEach(q => {
        questions.push({
          id: q.id,
          question: q.question,
          answer: q.answer,
          section: sectionKey
        });
      });
    });
    setAllQuestions(questions);
    setFilteredQuestions(questions);
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredQuestions(allQuestions);
      return;
    }

    const lowercaseQuery = searchQuery.toLowerCase();
    const filtered = allQuestions.filter(
      q => q.question.toLowerCase().includes(lowercaseQuery) || 
           q.answer.toLowerCase().includes(lowercaseQuery)
    );
    setFilteredQuestions(filtered);
  }, [searchQuery, allQuestions]);

  const toggleQuestion = (id: string) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const selectSection = (section: string) => {
    setActiveSection(section);
    setMobileMenuOpen(false);
    setSearchQuery('');
  };

  const renderSidebar = () => (
    <div className={`w-64 bg-gray-100 h-full overflow-y-auto flex-shrink-0 ${isDesktop ? 'block' : 'hidden'}`}>
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-center mb-4">
          <PrimeLogo size="large" />
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Pesquisar..."
            className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
        </div>
      </div>
      <nav className="p-4">
        {Object.keys(faqSections).map((sectionKey) => (
          <div key={sectionKey} className="mb-2">
            <button
              className={`flex items-center w-full text-left p-2 rounded-md ${
                activeSection === sectionKey ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => selectSection(sectionKey)}
            >
              <span className="mr-2">{faqSections[sectionKey].icon}</span>
              <span>{faqSections[sectionKey].title}</span>
            </button>
          </div>
        ))}
      </nav>
    </div>
  );

  const renderMobileMenu = () => (
    <div className={isDesktop ? 'hidden' : 'block'}>
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center">
          <PrimeLogo size="small" />
        </div>
        <button onClick={toggleMobileMenu} className="p-2">
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {mobileMenuOpen && (
        <div className="p-4 bg-gray-100">
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Pesquisar..."
              className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
          <nav>
            {Object.keys(faqSections).map((sectionKey) => (
              <div key={sectionKey} className="mb-2">
                <button
                  className={`flex items-center w-full text-left p-2 rounded-md ${
                    activeSection === sectionKey ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => selectSection(sectionKey)}
                >
                  <span className="mr-2">{faqSections[sectionKey].icon}</span>
                  <span>{faqSections[sectionKey].title}</span>
                </button>
              </div>
            ))}
          </nav>
        </div>
      )}
    </div>
  );

  const renderMainContent = () => {
    if (searchQuery.trim() !== '') {
      if (filteredQuestions.length === 0) {
        return (
          <div className="p-4 md:p-8">
            <h2 className="text-xl md:text-2xl font-bold mb-6">Resultados da pesquisa</h2>
            <p>Nenhum resultado encontrado para "{searchQuery}".</p>
          </div>
        );
      }

      return (
        <div className="p-4 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold mb-6">Resultados da pesquisa</h2>
          {filteredQuestions.map((q) => (
            <div key={q.id} className="mb-6 border-b pb-6">
              <button
                className="flex justify-between items-start w-full text-left font-medium text-gray-900 mb-2"
                onClick={() => toggleQuestion(q.id)}
              >
                <span className="flex-1 text-base md:text-lg">{q.question}</span>
                {expandedQuestions[q.id] ? 
                  <ChevronUp className="flex-shrink-0 h-5 w-5 text-indigo-500" /> : 
                  <ChevronDown className="flex-shrink-0 h-5 w-5 text-indigo-500" />
                }
              </button>
              {expandedQuestions[q.id] && (
                <div className="text-gray-700 mt-2 whitespace-pre-line">
                  {q.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    const currentSection = faqSections[activeSection];

    return (
      <div className="p-4 md:p-8">
        <h2 className="text-xl md:text-2xl font-bold mb-6">{currentSection?.icon || ''} {currentSection.title}</h2>
        {currentSection.questions.map((q) => (
          <div key={q.id} className="mb-6 border-b pb-6">
            <button
              className="flex justify-between items-start w-full text-left font-medium text-gray-900 mb-2"
              onClick={() => toggleQuestion(q.id)}
            >
              <span className="flex-1 text-base md:text-lg">{q.question}</span>
              {expandedQuestions[q.id] ? 
                <ChevronUp className="flex-shrink-0 h-5 w-5 text-indigo-500" /> : 
                <ChevronDown className="flex-shrink-0 h-5 w-5 text-indigo-500" />
              }
            </button>
            {expandedQuestions[q.id] && (
              <div className="text-gray-700 mt-2 whitespace-pre-line">
                {q.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {renderMobileMenu()}
      <div className="flex flex-1 overflow-hidden">
        {renderSidebar()}
        <div className="flex-1 overflow-y-auto">
          {renderMainContent()}
        </div>
      </div>
      <footer className="bg-gray-100 p-4 text-center text-gray-600 text-sm">
        <p>© {new Date().getFullYear()} Prime Cursos do Brasil - CNPJ: 09.408.027/0001-92</p>
        <p>Para mais informações, visite <a href="https://www.primecursos.com.br" className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">www.primecursos.com.br</a></p>
      </footer>
    </div>
  );
};

export default PrimeCursosFAQ;